import { tableFiltersUrl } from '../../utils/helper'
import request from '../axiosInterceptor'

const WorkflowBuilderModule = {
  getWorkflowForms(subsidiaryMasterId: string, data?: any) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms?subsidiary_master_id=${subsidiaryMasterId}${data ? `&${tableFiltersUrl(data)}` : ''}`,
      method: 'get',
    })
  },

  createWorkflowForm(payload: { name: string; workflow_type: string }) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms`,
      method: 'post',
      data: payload,
    })
  },

  getMasterFields(params: { workflow_type?: string; field_category?: string } = {}) {
    const qs = new URLSearchParams({
      workflow_type: params.workflow_type || 'statistics_capture',
      field_category: params.field_category || 'user_input',
    }).toString()
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/master-fields?${qs}`,
      method: 'get',
    })
  },

  createMasterField(payload: {
    name: string
    field_key: string
    data_type: string
    field_category: string
    workflow_type: string
    placeholder?: string
    help_text?: string
    default_value?: string
    config?: any
    expression?: string
  }) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/master-fields`,
      method: 'post',
      data: payload,
    })
  },

  getFormCanvas(formId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}`,
      method: 'get',
    })
  },

  // Component palette (base layer) — drives the builder's left pane. Replaces
  // the hard-coded frontend field-type list with the backend-served descriptors.
  getComponents(workflowType: string) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/components?workflow_type=${encodeURIComponent(workflowType)}`,
      method: 'get',
    })
  },

  saveFormCanvas(
    formId: number,
    payload: {
      name?: string
      sections: Array<{
        id?: number
        name: string
        sequence: number
        fields: Array<{
          id?: number
          sequence: number
          required?: boolean
          show_in_app?: boolean
          // Statistics Capture places an existing reusable master field by id.
          master_field_id?: number
          // Inspection / Audit send an inline field definition instead: the
          // backend create-or-updates a custom field_master per question on
          // save. `field_id` set → update that custom master; absent → create.
          field_id?: number
          data_type?: string
          label?: string
          config?: any
        }>
      }>
    },
  ) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}`,
      method: 'put',
      data: payload,
    })
  },

  updateMasterFieldExpression(fieldId: number, payload: { expression: string }) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/master-fields/${fieldId}`,
      method: 'patch',
      data: payload,
    })
  },

  publishForm(formId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/publish`,
      method: 'post',
    })
  },

  duplicateForm(formId: number, name?: string) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/duplicate`,
      method: 'post',
      data: { name: name || null },
    })
  },

  attachFormToProject(formId: number, projectDetailsId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/projects/${projectDetailsId}`,
      method: 'post',
    })
  },

  detachFormFromProject(formId: number, projectDetailsId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/projects/${projectDetailsId}`,
      method: 'delete',
    })
  },

  getFormProjects(formId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/projects`,
      method: 'get',
    })
  },

  getProjectForms(projectDetailsId: number, workflowType?: string) {
    const typeQuery = workflowType ? `?workflow_type=${encodeURIComponent(workflowType)}` : ''
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/projects/${projectDetailsId}/forms${typeQuery}`,
      method: 'get',
    })
  },

  getFormSchedules(formId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/schedules`,
      method: 'get',
    })
  },

  getProjectFormSchedules(formId: number, projectDetailsId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/projects/${projectDetailsId}/schedules`,
      method: 'get',
    })
  },

  deactivateForm(formId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/deactivate`,
      method: 'post',
    })
  },

  reactivateForm(formId: number) {
    return request({
      url: `${process.env.REACT_APP_API_BASEURL}v1/workflow-builder/forms/${formId}/reactivate`,
      method: 'post',
    })
  },
}

export default WorkflowBuilderModule
